import React, { useState } from 'react';

/**
 * SafeImg — <img> with graceful fallback.
 * If the image fails to load, replaces it with an SVG placeholder
 * that shows an icon on a themed background so the layout never breaks.
 *
 * Props:
 *  - src, alt, className, loading, style, sizes → standard <img> props
 *  - fallbackBg (default '#2F4F2C') background of placeholder
 *  - fallbackFg (default '#F5F0E5') icon colour
 *  - fallbackIcon: 'farm' | 'meat' | 'mountain' | 'cow' — chooses placeholder glyph
 */
const glyphs = {
  farm:
    '<path d="M35 60 L35 40 L50 30 L65 40 L65 60 Z" fill="none" stroke="{FG}" stroke-width="2.5"/><path d="M45 60 L45 50 L55 50 L55 60" fill="none" stroke="{FG}" stroke-width="2"/><path d="M28 62 L72 62" stroke="{FG}" stroke-width="2"/>',
  meat:
    '<circle cx="50" cy="50" r="18" fill="none" stroke="{FG}" stroke-width="2.5"/><path d="M42 45 Q46 40 50 45 Q54 40 58 45" fill="none" stroke="{FG}" stroke-width="2"/>',
  mountain:
    '<path d="M20 65 L35 45 L45 55 L55 40 L65 50 L80 65 Z" fill="none" stroke="{FG}" stroke-width="2.5" stroke-linejoin="round"/><path d="M35 45 L40 50" stroke="{FG}" stroke-width="1.5"/>',
  cow:
    '<ellipse cx="50" cy="55" rx="22" ry="12" fill="none" stroke="{FG}" stroke-width="2.5"/><circle cx="42" cy="52" r="1.8" fill="{FG}"/><circle cx="58" cy="52" r="1.8" fill="{FG}"/><path d="M35 45 Q32 40 34 37" fill="none" stroke="{FG}" stroke-width="2"/><path d="M65 45 Q68 40 66 37" fill="none" stroke="{FG}" stroke-width="2"/>',
};

const buildFallback = (bg, fg, icon) => {
  const g = (glyphs[icon] || glyphs.farm).replace(/\{FG\}/g, fg);
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' preserveAspectRatio='xMidYMid slice'>` +
    `<rect width='100' height='100' fill='${bg}'/>` +
    g +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const SafeImg = ({
  src,
  alt = '',
  className = '',
  loading = 'lazy',
  style,
  fallbackBg = '#2F4F2C',
  fallbackFg = '#F5F0E5',
  fallbackIcon = 'farm',
  ...rest
}) => {
  const [failed, setFailed] = useState(false);
  // If the src is missing/empty, render the fallback immediately —
  // <img src=""> would otherwise render nothing and never fire onError.
  const isMissing = !src || (typeof src === 'string' && src.trim() === '');
  const effectiveSrc = (failed || isMissing)
    ? buildFallback(fallbackBg, fallbackFg, fallbackIcon)
    : src;

  return (
    <img
      src={effectiveSrc}
      alt={alt}
      loading={loading}
      className={className}
      style={style}
      onError={() => { if (!failed) setFailed(true); }}
      {...rest}
    />
  );
};

export default SafeImg;
